import { FloorRule } from '@pubstack/common/src/adm/rules'
import { BIDDER_RULE_SECTION_LABELS } from '@pubstack/common/src/bidder-rule'
import { MediaType } from '@pubstack/common/src/media-types'
import { FLOOR_PRICE_NO_RULE_ID } from '@pubstack/common/src/pbs/floor-rule'
import { Site } from '@pubstack/common/src/site'
import { FormattedBidParams, isAdmStack, isPbsStack, Stack } from '@pubstack/common/src/stack'
import { cn } from '@pubstack/ui/utils/cn'
import { isEqual } from 'lodash-es'
import { Fragment, FunctionComponent, PropsWithChildren, useMemo } from 'react'
import { useContexts } from '~/api/adm/contexts.api'
import { useFloorRules } from '~/api/app/floor-rules.api'
import { useAdUnits as useAdmAdUnits } from '~/api/adm/ad-units.api'
import { useStackInventory } from '~/api/useStackInventory'
import { useLazyLoadingRules } from '~/api/adm/lazy-loading-rules.api'
import { useRefreshRules } from '~/api/adm/refresh-rules.api'
import { useBidderRule } from '~/api/bidder-rules.api'
import { useBidderSeats } from '~/api/bidder-seats.api'
import { useStack } from '~/api/stacks.api'
import { BidderMediaTypeList } from '~/feature-components/BidderMediaTypeList'
import { mediaTypesByBidderName, toDisplayedBidderRule } from '~/modules/monetization/inventory/bidder-rules/bidderRuleSections'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import { Flyout } from '~/components/Flyout'
import { Icon } from '~/components/Icon'
import { IconWithTooltip } from '~/components/IconWithTooltip'
import { MediaTypeBadge } from '~/components/MediaTypeBadge'
import { Spinner } from '~/components/Spinner'
import { OutOfSync } from '~/components/Adm/OutOfSync'
import { Tooltip } from '~/components/Tooltip'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { ContextConditions } from '~/modules/adm/ContextConditions'
import { RichDetails } from '~/modules/adm/inventory/sites/stack/components/RichDetails'
import { diff } from '~/utils/object'
import { admBidderGroups, admOutputOf } from './admServerside'
import { adUnitsToBidders, uniqueBidders } from './useStackChangelog'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'

const pipeSeparator = `\xa0\xa0|\xa0\xa0`

/** The h2 and h3 of a block are styled from here rather than one by one: every heading of a block reads the same. */
const DetailsWrapper: FunctionComponent<PropsWithChildren> = ({ children }) => (
  <div
    className={
      'flex max-w-[816px] flex-col gap-3 pt-2 pb-5 font-roboto text-base/[26px] [&_h2]:font-roboto [&_h2]:text-base/[26px] [&_h2]:font-medium [&_h3]:font-roboto [&_h3]:text-sm/[22px] [&_h3]:font-normal [&_h3]:text-slate-grey'
    }
  >
    {children}
  </div>
)

const TextIcon: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'inline-flex flex-wrap items-center gap-1'}>{children}</div>

const Title: FunctionComponent<PropsWithChildren> = ({ children }) => <h2 className={'font-rubik text-base/[26px] font-medium'}>{children}</h2>

const StackDetailWrapper: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'flex'}>{children}</div>

const StackDetailBlock: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'flex flex-col gap-0.5 pr-5'}>{children}</div>

const Chips: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'mt-2 flex flex-wrap gap-1'}>{children}</div>

/** Sits inside the tooltip's own padding, hence the negative margin. A caller may narrow or widen it with `className`. */
export const TooltipContent: FunctionComponent<PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <div className={cn('-m-2.5 flex w-[400px] flex-col gap-4 rounded bg-white p-3 text-jet shadow-toast', className)}>{children}</div>
)

const SectionTitle: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'mb-1 font-roboto text-sm/[22px] font-bold text-jet'}>{children}</div>

const Flex: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'flex flex-wrap items-center gap-1'}>{children}</div>

const NoBidders: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'flex items-center gap-1'}>{children}</div>

const BiddersBlock: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'flex flex-col gap-3'}>{children}</div>

const NoValueText: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'text-ash italic'}>{children}</div>

const FloorRuleSection: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'flex flex-col gap-1.5'}>{children}</div>

const OutOfSyncTitle: FunctionComponent<PropsWithChildren> = ({ children }) => <div className={'mb-1 flex items-center font-roboto text-sm/[22px] text-warning'}>{children}</div>

const Hr: FunctionComponent = () => <hr className={'w-full border-t border-platinum'} />

type BidderChipProps = {
  bidderName: string
  bidderOutput: Record<string, { mediaTypes: MediaType[]; params: FormattedBidParams }>
  bidderSnapshot: Record<string, { mediaTypes: MediaType[]; params: FormattedBidParams }>
  bidAdjustment?: number
}

const BidderChip: FunctionComponent<BidderChipProps> = ({ bidderName, bidderOutput, bidderSnapshot, bidAdjustment }) => {
  // A snapshot is canonical when it leaves the compiler, so a bidder is compared as it lies — the same
  // equality the backend uses to set `isOutOfSync`.
  const isOutOfSync = useMemo(() => !isEqual(bidderOutput, bidderSnapshot), [bidderOutput, bidderSnapshot])
  return (
    <Tooltip
      key={bidderName}
      className={'inline-flex!'}
      offset={8}
      title={
        <TooltipContent>
          <div>
            <SectionTitle>Mapped with</SectionTitle>
            <Flex>{Object.keys(bidderOutput).length > 0 ? Object.keys(bidderOutput).map((adUnitName) => <Chip key={adUnitName} text={adUnitName} color={Colors.Ghost} />) : 'None'}</Flex>
          </div>
          <div>
            <SectionTitle>Activated mediatypes</SectionTitle>
            <Flex>
              {Object.values(bidderOutput).length > 0
                ? Object.values(bidderOutput)
                    .reduce((acc: MediaType[], e) => {
                      return Array.from(new Set([...acc, ...e.mediaTypes]))
                    }, [])
                    .map((mt) => <MediaTypeBadge key={mt} mediaType={mt} variant="success" />)
                : 'None'}
            </Flex>
          </div>
          {isOutOfSync && (
            <>
              <Hr />
              <div>
                <OutOfSyncTitle>
                  <Icon name="sync_problem" height="1.25rem" width="1.25rem" />
                  Out of sync
                </OutOfSyncTitle>
                <Flex>
                  {Object.keys(diff(bidderOutput, bidderSnapshot)).map((adUnitName) => (
                    <Chip key={adUnitName} text={adUnitName} color={Colors.Ghost} />
                  ))}
                </Flex>
              </div>
            </>
          )}
          <div>
            <SectionTitle>Bid adjustment</SectionTitle>
            <div>{bidAdjustment !== undefined ? `${(bidAdjustment * 100).toFixed(0)}%` : 'None'}</div>
          </div>
        </TooltipContent>
      }
    >
      <Chip
        iconLeft={isOutOfSync ? { name: 'sync_problem', fill: Colors.Warning, stroke: Colors.Warning, width: '1.125rem', height: '1.125rem' } : undefined}
        // `!` because `Chip` is Emotion, whose unlayered background wins over a utility.
        className={isOutOfSync ? 'bg-warning/12!' : undefined}
        text={bidderName}
        color={isOutOfSync ? Colors.Warning : Colors.Ghost}
      />
    </Tooltip>
  )
}

type Props = {
  site: Site
  stack: Stack
  onEditStack?: (event: React.MouseEvent<HTMLElement>) => void
}

export const ViewStackFlyout: FunctionComponent<Props> = ({ site, stack: providedStack, onEditStack }) => {
  const { canEdit, disabledReason } = useScopePermissions()
  // Site listings leave out `snapshot`/`output`, which this flyout needs: fetch the one stack it shows.
  const needsFullStack = providedStack.output === undefined
  const { data: fetchedStack } = useStack(needsFullStack ? providedStack.id : undefined)
  const stack = fetchedStack ?? providedStack

  /**
   * The stack's own type, not `useProduct()`.
   *
   * The flyout opens from product paths today, where the two always agree, but it is also opened from the
   * deployment center, which is product-neutral — and there `useProduct()` falls back to `adm` (see its
   * docblock). A Node stack would then lose the `env` narrowing on its ad units below and report an `adm`
   * tracking label. Asking the stack is right on every path, so nothing has to remember which one it is on.
   *
   * `adm` or else pbs-flavoured, mirroring how the rest of this component branches: the two scope-level
   * types carry Prebid Server output just as `pbs` does.
   */
  const product = isAdmStack(stack) ? 'adm' : 'pbs'
  // Tracking labels
  const EDIT_TRACKING_LABEL: TrackingLabel = `${product}-sites-stacks-edit`

  // API Hooks
  // One hook for both stack types: the two rule collections became one, so the id resolves the same
  // way whichever product the stack belongs to.
  const { data: bidderRule, isLoading: isBidderRuleLoading } = useBidderRule(isAdmStack(stack) ? 'adm' : 'pbs', stack.configuration.bidderRuleId)
  // Both products' seats: the rule spans all three sections, and a One stack can carry `-s2s` ones.
  const { data: admBidderSeats } = useBidderSeats('adm')
  const { data: pbsBidderSeats } = useBidderSeats('pbs')
  const grantedMediaTypes = useMemo(() => mediaTypesByBidderName(bidderRule, [...admBidderSeats, ...pbsBidderSeats]), [bidderRule, admBidderSeats, pbsBidderSeats])
  // Hovering the rule name shows its bidders, the way a floor or lazy loading rule already does.
  const displayedBidderRule = useMemo(() => (bidderRule ? toDisplayedBidderRule(bidderRule, [...admBidderSeats, ...pbsBidderSeats]) : undefined), [bidderRule, admBidderSeats, pbsBidderSeats])
  const { data: floorRules, isLoading: floorRulesLoading } = useFloorRules()
  const { data: contexts } = useContexts()
  const context = contexts.find((c) => c.id === stack.configuration.contextId)
  // The floor rule sections below resolve a stored ad unit id to a name, so this has to be the inventory
  // the stack was built from: `SITE_AD_UNIT_ENV_COMPATIBILITY` gives a web media both `web` and `s2s`, which
  // a stack never mixes. Gated on the media rather than on its `env`, which the oldest ones do not carry.
  const { adUnits, isLoading: adUnitsLoading } = useStackInventory(site)
  const { data: admAdUnits } = useAdmAdUnits()
  const admAdUnitsByName = useMemo(() => new Map(admAdUnits.map((u) => [u.name, u])), [admAdUnits])
  const admAdUnitsById = useMemo(() => new Map(admAdUnits.map((u) => [u.id, u])), [admAdUnits])
  const { data: lazyLoadingRules } = useLazyLoadingRules()
  const { data: refreshRules } = useRefreshRules()
  const currencySymbol = useScopeCurrency()

  // PBS-specific
  const bidderNames = isPbsStack(stack) ? uniqueBidders(stack.output?.adUnits) : []
  const stackBidders = isPbsStack(stack) ? adUnitsToBidders(stack.output?.adUnits) : {}
  const liveStackBidders = isPbsStack(stack) ? adUnitsToBidders(stack.snapshot?.adUnits) : {}

  const adUnitsUsedInSpecificFloorRules = useMemo(
    () => Array.from(new Set(stack.configuration.floorRuleConfig?.specificFloorRuleConfigs?.flatMap((rule) => rule.adUnitIds))),
    [stack.configuration.floorRuleConfig]
  )

  const floorRulesWithNoRule: FloorRule[] = [...floorRules, { id: FLOOR_PRICE_NO_RULE_ID, name: 'No floor price' } as FloorRule]
  const standardRule = floorRulesWithNoRule.find((f) => f.id === stack.configuration.floorRuleConfig?.standardFloorRuleId)

  // ADM-specific
  const admOutput = useMemo(() => admOutputOf(stack), [stack])
  const admDesktopAdUnitNames = useMemo(() => admOutput?.desktop.adUnits.map((u) => u.adUnitName) ?? [], [admOutput])
  const admMobileAdUnitNames = useMemo(() => admOutput?.mobile.adUnits.map((u) => u.adUnitName) ?? [], [admOutput])
  const admServersideAdUnitNames = useMemo(() => Object.keys(admOutput?.serverside?.adUnits ?? {}), [admOutput])
  const admGroups = useMemo(() => admBidderGroups(admOutput), [admOutput])
  const serversideOutputBidders = useMemo(() => adUnitsToBidders(isAdmStack(stack) ? stack.output?.serverside?.adUnits : undefined), [stack])
  const serversideSnapshotBidders = useMemo(() => adUnitsToBidders(isAdmStack(stack) ? stack.snapshot.serverside?.adUnits : undefined), [stack])

  const isHeaderBiddingEnabled = isAdmStack(stack) && stack.configuration.headerBiddingEnabled
  const isLazyLoadingEnabled = !!(admOutput?.desktop.lazyLoading || admOutput?.mobile.lazyLoading)
  const isRefreshEnabled = !!(admOutput?.desktop.refreshConfig || admOutput?.mobile.refreshConfig)
  const admLazyLoadingDesktopRule = useMemo(() => {
    if (!isAdmStack(stack)) return undefined
    const ruleId = stack.configuration.lazyLoadingRuleConfig?.find((c) => c.device === 'desktop')?.ruleId
    return lazyLoadingRules.find((r) => r.id === ruleId)
  }, [stack, lazyLoadingRules])
  const admLazyLoadingMobileRule = useMemo(() => {
    if (!isAdmStack(stack)) return undefined
    const ruleId = stack.configuration.lazyLoadingRuleConfig?.find((c) => c.device === 'mobile')?.ruleId
    return lazyLoadingRules.find((r) => r.id === ruleId)
  }, [stack, lazyLoadingRules])

  const getBidderDifferences = (bidderName: string): string[] | undefined => {
    if (!stack.isOutOfSync) return undefined
    if (!isAdmStack(stack) || !stack.output) return []
    const diffs = new Set<string>()
    ;(['desktop', 'mobile'] as const).forEach((device) => {
      const outAdjustment = stack.output![device].prebidConfig.pbjsConfig.bidderAdjustments?.find((a) => a.bidder === bidderName)
      const snapAdjustment = stack.snapshot[device].prebidConfig.pbjsConfig.bidderAdjustments?.find((a) => a.bidder === bidderName)
      if (!isEqual(outAdjustment, snapAdjustment)) diffs.add('bid adjustment updated')
      const outMapping = stack.output![device].adUnits.map((u) => ({ adUnitName: u.adUnitName, params: u.bidders.find((b) => b.bidder === bidderName)?.params })).filter((m) => m.params !== undefined)
      const snapMapping = stack.snapshot[device].adUnits
        .map((u) => ({ adUnitName: u.adUnitName, params: u.bidders.find((b) => b.bidder === bidderName)?.params }))
        .filter((m) => m.params !== undefined)
      if (!isEqual(outMapping, snapMapping)) diffs.add('mapping updated')
    })
    return Array.from(diffs)
  }
  const getServersideBidderDifferences = (bidderName: string): string[] | undefined => {
    if (!stack.isOutOfSync) return undefined
    if (!isAdmStack(stack) || !stack.output) return []
    return isEqual(serversideOutputBidders[bidderName], serversideSnapshotBidders[bidderName]) ? [] : ['mapping updated']
  }
  const getServersideAdUnitDifferences = (name: string): string[] | undefined => {
    if (!stack.isOutOfSync) return undefined
    if (!isAdmStack(stack) || !stack.output) return []
    return isEqual(stack.output.serverside?.adUnits[name], stack.snapshot.serverside?.adUnits[name]) ? [] : ['bidders updated']
  }
  const getAdUnitDifferences = (name: string, device: 'desktop' | 'mobile'): string[] | undefined => {
    if (!stack.isOutOfSync) return undefined
    if (!isAdmStack(stack)) return []
    const out = stack.output?.[device].adUnits.find((u) => u.adUnitName === name)
    const snap = stack.snapshot[device].adUnits.find((u) => u.adUnitName === name)
    if (!out || !snap) return []
    const diffs: string[] = []
    if (!isEqual(out.bidders, snap.bidders)) diffs.push('bidders updated')
    if (!isEqual(out.sizes, snap.sizes)) diffs.push('sizes updated')
    if (!isEqual(out.floors, snap.floors)) diffs.push('floor price updated')
    if (out.headerBiddingEnabled !== snap.headerBiddingEnabled) diffs.push('header bidding setting updated')
    if (!isEqual(out.targeting, snap.targeting)) diffs.push('targeting updated')
    return diffs
  }
  const getLazyLoadingDifferences = (device: 'desktop' | 'mobile'): string[] | undefined => {
    if (!stack.isOutOfSync) return undefined
    if (!isAdmStack(stack)) return []
    return isEqual(stack.output?.[device].lazyLoading, stack.snapshot[device].lazyLoading) ? [] : ['lazy loading rule updated']
  }
  const getFloorRuleDifferences = (adUnitNames: string[], device?: 'desktop' | 'mobile'): string[] | undefined => {
    if (!stack.isOutOfSync) return undefined
    if (isAdmStack(stack) && device && stack.output) {
      const out = stack.output[device].adUnits.filter((u) => adUnitNames.includes(u.adUnitName)).map((u) => [u.adUnitName, u.floors])
      const snap = stack.snapshot[device].adUnits.filter((u) => adUnitNames.includes(u.adUnitName)).map((u) => [u.adUnitName, u.floors])
      return isEqual(out, snap) ? [] : ['floor price updated']
    }
    if (isPbsStack(stack) && stack.output) {
      const out = Object.entries(stack.output.floors?.rulesByAdUnit ?? {}).filter(([a]) => adUnitNames.includes(a))
      const snap = Object.entries(stack.snapshot.floors?.rulesByAdUnit ?? {}).filter(([a]) => adUnitNames.includes(a))
      return isEqual(out, snap) ? [] : ['floor price updated']
    }
    return []
  }
  const getAdmStandardFloorAdUnitNames = (device: 'desktop' | 'mobile'): string[] => {
    if (!isAdmStack(stack)) return []
    const config = device === 'desktop' ? stack.configuration.floorRuleConfig : stack.configuration.mobileFloorRuleConfig
    const allNames = (admOutput?.[device].adUnits ?? []).map((u) => u.adUnitName)
    const specificIds = config?.specificFloorRuleConfigs?.flatMap((r) => r.adUnitIds) ?? []
    const specificNames = specificIds.map((id) => admAdUnitsById.get(id)?.name).filter((n): n is string => !!n)
    return allNames.filter((n) => !specificNames.includes(n))
  }
  const buildFloorRulePayload = (rule: FloorRule | undefined, clearSizes: boolean) => {
    if (!rule || rule.id === FLOOR_PRICE_NO_RULE_ID) return undefined
    return {
      adUnitNames: [],
      ...rule,
      ruleName: rule.name,
      floor: `${rule.floor}`,
      settings: rule.settings?.map((s) => ({ ...s, sizes: clearSizes ? [] : s.sizes })),
    }
  }
  const getRefreshDifferences = (ruleId: string, device: 'desktop' | 'mobile'): string[] | undefined => {
    if (!stack.isOutOfSync) return undefined
    if (!isAdmStack(stack)) return []
    const adUnitIds = (stack.configuration.refreshRuleConfig ?? []).filter((c) => c.device === device && c.ruleId === ruleId).map((c) => c.adUnitId)
    const adUnitNames = adUnitIds.map((id) => admAdUnitsById.get(id)?.name).filter((n): n is string => !!n)
    const outRules = (stack.output?.[device].refreshConfig?.adUnitConfig ?? []).filter((r) => r.adUnitNames.some((n) => adUnitNames.includes(n)))
    const snapRules = (stack.snapshot[device].refreshConfig?.adUnitConfig ?? []).filter((r) => r.adUnitNames.some((n) => adUnitNames.includes(n)))
    return isEqual(outRules, snapRules) ? [] : ['refresh rule updated']
  }

  const admRefreshGroupsByDevice = useMemo(() => {
    const empty = { desktop: [] as { rule: (typeof refreshRules)[number]; adUnitIds: string[] }[], mobile: [] as { rule: (typeof refreshRules)[number]; adUnitIds: string[] }[] }
    if (!isAdmStack(stack)) return empty
    const groupBy = (device: 'desktop' | 'mobile') => {
      const groups = new Map<string, string[]>()
      ;(stack.configuration.refreshRuleConfig ?? []).forEach((c) => {
        if (c.device !== device || !c.ruleId) return
        if (!groups.has(c.ruleId)) groups.set(c.ruleId, [])
        groups.get(c.ruleId)!.push(c.adUnitId)
      })
      return Array.from(groups.entries())
        .map(([ruleId, adUnitIds]) => {
          const rule = refreshRules.find((r) => r.id === ruleId)
          return rule ? { rule, adUnitIds } : null
        })
        .filter((v): v is { rule: (typeof refreshRules)[number]; adUnitIds: string[] } => v !== null)
    }
    return { desktop: groupBy('desktop'), mobile: groupBy('mobile') }
  }, [stack, refreshRules])
  // The sections of the bidder rule, which is how the stack's bidders are grouped. The server-side seats come from
  // the variant rather than the device configs, so their differences are read from there.
  const admBidderSections = [
    { section: 'client_side', icon: 'client', names: admGroups.clientSide, getDifferences: getBidderDifferences },
    { section: 'server_side_deprecated', icon: 'server', names: admGroups.legacyServerSide, getDifferences: getBidderDifferences },
    { section: 'server_side', icon: 'server', names: admGroups.serverSide, getDifferences: getServersideBidderDifferences },
  ] as const
  const admMobileStandardRule = isAdmStack(stack) ? floorRulesWithNoRule.find((f) => f.id === stack.configuration.mobileFloorRuleConfig?.standardFloorRuleId) : undefined
  const hasAdmFloorPrice = isAdmStack(stack) && (!!stack.configuration.floorRuleConfig || !!stack.configuration.mobileFloorRuleConfig)

  return (
    <Flyout.Content>
      <Flyout.Title>
        {stack.name} v{stack.version}
      </Flyout.Title>
      <Flyout.Body>
        <Title>Infos</Title>
        <DetailsWrapper>
          <StackDetailWrapper>
            <StackDetailBlock>
              <h3>Media</h3>
              <div>{site.name}</div>
            </StackDetailBlock>
            <StackDetailBlock>
              <h3>Context</h3>
              {context?.conditions ? (
                <Tooltip reposition={true} className={'inline-flex!'} title={<RichDetails conditions={<ContextConditions group={context.conditions} />} />} align={'start'}>
                  <span>{context.name}</span>
                </Tooltip>
              ) : (
                <span>{context?.name || 'default'}</span>
              )}
            </StackDetailBlock>
          </StackDetailWrapper>
        </DetailsWrapper>

        {isAdmStack(stack) ? (
          <>
            <Title>Ad units</Title>
            <DetailsWrapper>
              <StackDetailBlock>
                <h3>Desktop</h3>
                <TextIcon>
                  {admDesktopAdUnitNames.map((name, index) => (
                    <Fragment key={name}>
                      <OutOfSync name={name} adUnit={admAdUnitsByName.get(name)} differences={getAdUnitDifferences(name, 'desktop')} />
                      {index < admDesktopAdUnitNames.length - 1 && pipeSeparator}
                    </Fragment>
                  ))}
                </TextIcon>
              </StackDetailBlock>
              <StackDetailBlock>
                <h3>Mobile</h3>
                <TextIcon>
                  {admMobileAdUnitNames.map((name, index) => (
                    <Fragment key={name}>
                      <OutOfSync name={name} adUnit={admAdUnitsByName.get(name)} differences={getAdUnitDifferences(name, 'mobile')} />
                      {index < admMobileAdUnitNames.length - 1 && pipeSeparator}
                    </Fragment>
                  ))}
                </TextIcon>
              </StackDetailBlock>
              {admOutput?.serverside && (
                <StackDetailBlock>
                  <h3>Server-side</h3>
                  <TextIcon>
                    {admServersideAdUnitNames.map((name, index) => (
                      <Fragment key={name}>
                        <OutOfSync name={name} adUnit={admAdUnitsByName.get(name)} differences={getServersideAdUnitDifferences(name)} />
                        {index < admServersideAdUnitNames.length - 1 && pipeSeparator}
                      </Fragment>
                    ))}
                  </TextIcon>
                </StackDetailBlock>
              )}
            </DetailsWrapper>

            <TextIcon>
              <Title>Header bidding</Title>
              <Icon width={'26px'} name={isHeaderBiddingEnabled ? 'check' : 'close'} fill={isHeaderBiddingEnabled ? Colors.Success : Colors.Hurricane} />
            </TextIcon>
            <DetailsWrapper>
              {isHeaderBiddingEnabled && (
                <>
                  <StackDetailBlock>
                    <h3>Timeout</h3>
                    <span>{admOutput?.desktop.prebidConfig.adCallTimeout} ms</span>
                  </StackDetailBlock>
                  <StackDetailBlock>
                    <h3>Server-side</h3>
                    <span>{admOutput?.serverside ? 'Calls Prebid Server through the pubstack bidder' : 'No server-side seat'}</span>
                  </StackDetailBlock>
                </>
              )}
            </DetailsWrapper>

            <Title>Integrations</Title>
            <DetailsWrapper>
              <StackDetailBlock>
                <TextIcon>
                  <h3>Bidder rule</h3>
                  <IconWithTooltip title="Configuration applied to the bid from bidders listed on the rule." name="info" fill={Colors.Hurricane} width="20px" align="center" />
                </TextIcon>
                <div>{isBidderRuleLoading ? <Spinner /> : bidderRule ? <OutOfSync name={bidderRule.name} bidderRule={displayedBidderRule} /> : null}</div>
              </StackDetailBlock>
              <StackDetailBlock>
                <TextIcon>
                  <h3>Prebid bidders</h3>
                  <IconWithTooltip title="At least one ad unit of the stack is mapped with those bidders for this site." name="info" fill={Colors.Hurricane} width="20px" align="center" />
                </TextIcon>
                {isHeaderBiddingEnabled ? (
                  <BiddersBlock>
                    {admBidderSections.map(({ section, icon, names, getDifferences }) =>
                      names.length > 0 ? (
                        <StackDetailBlock key={section}>
                          <TextIcon>
                            <TextIcon>
                              <Icon width={'16px'} name={icon} fill={Colors.Hurricane} />
                              <h3>{BIDDER_RULE_SECTION_LABELS[section]}</h3>
                            </TextIcon>
                            <IconWithTooltip title="Bidders of this section to which ad units are mapped on this site." name="info" fill={Colors.Hurricane} width="16px" align="center" />
                          </TextIcon>
                          <BidderMediaTypeList
                            rows={names.map((name) => ({
                              key: name,
                              label: <OutOfSync name={name} differences={getDifferences(name)} />,
                              mediaTypes: grantedMediaTypes[name] ?? [],
                            }))}
                          />
                        </StackDetailBlock>
                      ) : null
                    )}
                    {admBidderSections.every(({ names }) => names.length === 0) && (
                      <NoBidders>
                        <Icon name="warning" fill={Colors.Warning} height="1.25rem" /> No bidders mapped
                      </NoBidders>
                    )}
                  </BiddersBlock>
                ) : (
                  <NoValueText>Header bidding disabled</NoValueText>
                )}
              </StackDetailBlock>
            </DetailsWrapper>

            <TextIcon>
              <Title>Floor price</Title>
              <Icon width={'26px'} name={hasAdmFloorPrice ? 'check' : 'close'} fill={hasAdmFloorPrice ? Colors.Success : Colors.Hurricane} />
            </TextIcon>
            {hasAdmFloorPrice ? (
              floorRulesLoading || adUnitsLoading ? (
                <DetailsWrapper>
                  <Spinner />
                </DetailsWrapper>
              ) : (
                <DetailsWrapper>
                  {stack.configuration.floorRuleConfig && (
                    <>
                      <h2>Desktop rules</h2>
                      <StackDetailBlock>
                        <h3>Standard rule</h3>
                        <OutOfSync
                          name={standardRule?.name ?? ''}
                          floorRule={buildFloorRulePayload(standardRule, true)}
                          differences={getFloorRuleDifferences(getAdmStandardFloorAdUnitNames('desktop'), 'desktop')}
                          currencySymbol={currencySymbol}
                        />
                      </StackDetailBlock>
                      {stack.configuration.floorRuleConfig.specificFloorRuleConfigs?.map((specificRule, index) => {
                        const usedAdUnits = admAdUnits.filter((adUnit) => specificRule.adUnitIds.includes(adUnit.id))
                        const usedAdUnitNames = usedAdUnits.map((u) => u.name)
                        const floorRule = floorRulesWithNoRule.find((f) => f.id === specificRule.floorRuleId)
                        return (
                          <FloorRuleSection key={specificRule.floorRuleId}>
                            <StackDetailBlock>
                              <h3>Specific rule {index + 1}</h3>
                              <OutOfSync
                                name={floorRule?.name ?? ''}
                                floorRule={buildFloorRulePayload(floorRule, false)}
                                differences={getFloorRuleDifferences(usedAdUnitNames, 'desktop')}
                                currencySymbol={currencySymbol}
                              />
                            </StackDetailBlock>
                            <StackDetailBlock>
                              <h3>Applied to</h3>
                              <Chips>
                                {usedAdUnits.map(({ name }) => (
                                  <Chip key={name} text={name} color={Colors.Ghost} />
                                ))}
                              </Chips>
                            </StackDetailBlock>
                          </FloorRuleSection>
                        )
                      })}
                    </>
                  )}
                  {stack.configuration.mobileFloorRuleConfig && (
                    <>
                      <h2>Mobile rules</h2>
                      <StackDetailBlock>
                        <h3>Standard rule</h3>
                        <OutOfSync
                          name={admMobileStandardRule?.name ?? ''}
                          floorRule={buildFloorRulePayload(admMobileStandardRule, true)}
                          differences={getFloorRuleDifferences(getAdmStandardFloorAdUnitNames('mobile'), 'mobile')}
                          currencySymbol={currencySymbol}
                        />
                      </StackDetailBlock>
                      {stack.configuration.mobileFloorRuleConfig.specificFloorRuleConfigs?.map((specificRule, index) => {
                        const usedAdUnits = admAdUnits.filter((adUnit) => specificRule.adUnitIds.includes(adUnit.id))
                        const usedAdUnitNames = usedAdUnits.map((u) => u.name)
                        const floorRule = floorRulesWithNoRule.find((f) => f.id === specificRule.floorRuleId)
                        return (
                          <FloorRuleSection key={specificRule.floorRuleId}>
                            <StackDetailBlock>
                              <h3>Specific rule {index + 1}</h3>
                              <OutOfSync
                                name={floorRule?.name ?? ''}
                                floorRule={buildFloorRulePayload(floorRule, false)}
                                differences={getFloorRuleDifferences(usedAdUnitNames, 'mobile')}
                                currencySymbol={currencySymbol}
                              />
                            </StackDetailBlock>
                            <StackDetailBlock>
                              <h3>Applied to</h3>
                              <Chips>
                                {usedAdUnits.map(({ name }) => (
                                  <Chip key={name} text={name} color={Colors.Ghost} />
                                ))}
                              </Chips>
                            </StackDetailBlock>
                          </FloorRuleSection>
                        )
                      })}
                    </>
                  )}
                </DetailsWrapper>
              )
            ) : (
              <DetailsWrapper />
            )}

            <TextIcon>
              <Title>Lazy loading</Title>
              <Icon width={'26px'} name={isLazyLoadingEnabled ? 'check' : 'close'} fill={isLazyLoadingEnabled ? Colors.Success : Colors.Hurricane} />
            </TextIcon>
            {isLazyLoadingEnabled ? (
              <DetailsWrapper>
                <StackDetailBlock>
                  <h3>Desktop rule</h3>
                  <TextIcon>
                    {admLazyLoadingDesktopRule ? (
                      <OutOfSync name={admLazyLoadingDesktopRule.name} lazyLoadingRule={admLazyLoadingDesktopRule} differences={getLazyLoadingDifferences('desktop')} />
                    ) : (
                      <span>No lazy loading</span>
                    )}
                  </TextIcon>
                </StackDetailBlock>
                <StackDetailBlock>
                  <h3>Mobile rule</h3>
                  <TextIcon>
                    {admLazyLoadingMobileRule ? (
                      <OutOfSync name={admLazyLoadingMobileRule.name} lazyLoadingRule={admLazyLoadingMobileRule} differences={getLazyLoadingDifferences('mobile')} />
                    ) : (
                      <span>No lazy loading</span>
                    )}
                  </TextIcon>
                </StackDetailBlock>
              </DetailsWrapper>
            ) : (
              <DetailsWrapper />
            )}

            <TextIcon>
              <Title>Refresh</Title>
              <Icon width={'26px'} name={isRefreshEnabled ? 'check' : 'close'} fill={isRefreshEnabled ? Colors.Success : Colors.Hurricane} />
            </TextIcon>
            {isRefreshEnabled && (
              <DetailsWrapper>
                {admRefreshGroupsByDevice.desktop.length > 0 && (
                  <>
                    <h2>Desktop rules</h2>
                    {admRefreshGroupsByDevice.desktop.map(({ rule, adUnitIds }, index) => (
                      <div key={rule.id}>
                        <div>
                          <h3>Desktop rule {index + 1}</h3>
                          <OutOfSync name={rule.name} differences={getRefreshDifferences(rule.id, 'desktop')} />
                        </div>
                        <div>
                          <h3>Rule applied to</h3>
                          <span>
                            {adUnitIds
                              .map((id) => admAdUnitsById.get(id)?.name)
                              .filter(Boolean)
                              .join(pipeSeparator)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {admRefreshGroupsByDevice.mobile.length > 0 && (
                  <>
                    <h2>Mobile rules</h2>
                    {admRefreshGroupsByDevice.mobile.map(({ rule, adUnitIds }, index) => (
                      <div key={rule.id}>
                        <div>
                          <h3>Mobile rule {index + 1}</h3>
                          <OutOfSync name={rule.name} differences={getRefreshDifferences(rule.id, 'mobile')} />
                        </div>
                        <div>
                          <h3>Rule applied to</h3>
                          <span>
                            {adUnitIds
                              .map((id) => admAdUnitsById.get(id)?.name)
                              .filter(Boolean)
                              .join(pipeSeparator)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </DetailsWrapper>
            )}

            {stack.commitMessage && (
              <>
                <Title>Comment</Title>
                <div>{stack.commitMessage}</div>
              </>
            )}
          </>
        ) : (
          <>
            <Title>Integrations</Title>
            <DetailsWrapper>
              <StackDetailBlock>
                <TextIcon>
                  <h3>Bidder rule</h3>
                  <IconWithTooltip title="Configuration applied to the bid from bidders listed on the rule." name="info" fill={Colors.Hurricane} width="20px" align="center" />
                </TextIcon>
                <div>{isBidderRuleLoading ? <Spinner /> : bidderRule ? <OutOfSync name={bidderRule.name} bidderRule={displayedBidderRule} /> : null}</div>
              </StackDetailBlock>
              <StackDetailBlock>
                <TextIcon>
                  <h3>Bidders</h3>
                  <IconWithTooltip title="Bidders to which ad units are mapped on this site." name="info" fill={Colors.Hurricane} width="20px" align="center" />
                </TextIcon>
                {bidderNames.length > 0 ? (
                  // The chip stays the row's label: its tooltip carries the mapped ad units and the
                  // out-of-sync diff, which the media types alongside it do not replace.
                  <BidderMediaTypeList
                    rows={bidderNames.map((bidderName) => ({
                      key: bidderName,
                      label: (
                        <BidderChip
                          bidderName={bidderName}
                          bidderOutput={stackBidders[bidderName]}
                          bidderSnapshot={liveStackBidders[bidderName]}
                          bidAdjustment={stack.output?.bidAdjustments[bidderName]}
                        />
                      ),
                      mediaTypes: grantedMediaTypes[bidderName] ?? [],
                    }))}
                  />
                ) : (
                  <NoBidders>
                    <Icon name="warning" fill={Colors.Warning} height="1.25rem" /> No bidders mapped
                  </NoBidders>
                )}
              </StackDetailBlock>
            </DetailsWrapper>
            {!!stack.configuration.floorRuleConfig && (
              <>
                <Title>Floor price</Title>
                {floorRulesLoading || adUnitsLoading ? (
                  <DetailsWrapper>
                    <Spinner />
                  </DetailsWrapper>
                ) : (
                  <DetailsWrapper>
                    <StackDetailBlock>
                      <h3>Standard rule</h3>
                      <OutOfSync
                        name={standardRule?.name ?? ''}
                        floorRule={buildFloorRulePayload(standardRule, true)}
                        differences={getFloorRuleDifferences(adUnits.filter((adUnit) => !adUnitsUsedInSpecificFloorRules.includes(adUnit.id)).map((a) => a.name))}
                        currencySymbol={currencySymbol}
                      />
                    </StackDetailBlock>
                    {stack.configuration.floorRuleConfig?.specificFloorRuleConfigs?.map((specificRule, index) => {
                      const usedAdUnits = adUnits.filter((adUnit) => specificRule.adUnitIds.includes(adUnit.id))
                      const floorRule = floorRulesWithNoRule.find((f) => f.id === specificRule.floorRuleId)
                      return (
                        <FloorRuleSection key={specificRule.floorRuleId}>
                          <StackDetailBlock>
                            <h3>Specific rule {index + 1}</h3>
                            <OutOfSync
                              name={floorRule?.name ?? ''}
                              floorRule={buildFloorRulePayload(floorRule, false)}
                              differences={getFloorRuleDifferences(usedAdUnits.map((a) => a.name))}
                              currencySymbol={currencySymbol}
                            />
                          </StackDetailBlock>
                          <StackDetailBlock>
                            <h3>Applied to</h3>
                            <Chips>
                              {usedAdUnits.map(({ name }) => (
                                <Chip key={name} text={name} color={Colors.Ghost} />
                              ))}
                            </Chips>
                          </StackDetailBlock>
                        </FloorRuleSection>
                      )
                    })}
                  </DetailsWrapper>
                )}
              </>
            )}
          </>
        )}
      </Flyout.Body>
      <Flyout.Actions>
        {/* Offered whatever the stack's status, live included: reading a stack is how a publisher decides
            to change it, and a live stack is the one most likely to need it. It used to appear only on a
            `to-deploy` stack, which left the common case with no way out of the flyout but to close it and
            find the stack again under its media.

            Editing is owner-only at the API, so a reader who cannot do it is not walked into a 403 — the
            reason is on the tooltip instead, which is how they learn there is something to ask for. */}
        {onEditStack && (
          <Tooltip title={canEdit ? 'Edit stack' : disabledReason} positions={['left']}>
            <Button variant={'secondary'} onClick={onEditStack} iconName={'edit'} disabled={!canEdit} trackingLabel={EDIT_TRACKING_LABEL}>
              Edit
            </Button>
          </Tooltip>
        )}
      </Flyout.Actions>
    </Flyout.Content>
  )
}
